import React, { useState } from 'react';
// mui
import {
  Box,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Button,
  Divider,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// components
import PricingCard from './PricingCard';
// data
import data from './config.json';

export default function Pricing() {
  const [subscription, setSubscription] = useState('12 Months');
  const [showDetails, setShowDetails] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const currentPlan = data.data.find(
    (plan) => plan.subscription === subscription
  );

  const handleSubscriptionChange = (newSubscription) => {
    setSubscription(newSubscription);
  };

  // Calculate savings for yearly plan
  const yearlyPlan = data.data.find(plan => plan.subscription === '12 Months');
  const monthlyPlan = data.data.find(plan => plan.subscription === 'Monthly');
  const yearlyPremium = yearlyPlan?.plan.find(p => p.name === 'Premium');
  const monthlyPremium = monthlyPlan?.plan.find(p => p.name === 'Premium');
  
  const monthlyCost = parseFloat(monthlyPremium?.limitedPrice?.replace('$', '') || '20');
  const yearlyCost = parseFloat(yearlyPremium?.limitedPrice?.replace('$', '') || '199');
  const monthlyEquivalent = yearlyCost / 12;
  const savings = ((monthlyCost - monthlyEquivalent) / monthlyCost * 100).toFixed(0);

  return (
    <Box maxWidth={1280} mx="auto" p={isMobile ? 1 : 2}>
      <Container maxWidth='xl'>
        <Card
          sx={{
            my: isMobile ? 2 : 8,
            position: 'relative',
            zIndex: 999,
            borderRadius: isMobile ? '8px' : '12px',
            boxShadow: 'rgba(99, 102, 241, 0.1) 0px 4px 24px',
            border: '1px solid',
            borderColor: 'grey.100'
          }}>
          <CardContent
            sx={{
              px: { xs: 1.5, sm: 2, md: 4.5 },
              py: { xs: 2, md: 4 },
            }}>
            
            {/* Header Section - Mobile Optimized */}
            <Stack
              mb={isMobile ? 2 : 3}
              spacing={isMobile ? 1 : 2}
              direction='column'
              alignItems='center'
              textAlign='center'>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant='h1'
                  fontSize={{ xs: 24, sm: 28, md: 40 }}
                  fontWeight={700}
                  color="text.primary"
                  sx={{ mb: 1, lineHeight: 1.2 }}>
                  AI-Powered Business Tools
                </Typography>
                <Typography
                  variant='h2'
                  fontSize={{ xs: 14, sm: 16, md: 18 }}
                  fontWeight={400}
                  color="text.secondary"
                  sx={{ mb: isMobile ? 2 : 0 }}>
                  AI content generation and review management for small businesses. Start free, upgrade for more.
                </Typography>
              </Box>
              
              {/* Mobile-First Subscription Toggle */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: '12px',
                  p: 0.5,
                  bgcolor: 'grey.50'
                }}>
                <Stack direction='row' spacing={0.5}>
                  {['12 Months', 'Monthly'].map((period) => (
                    <Button
                      key={period}
                      variant={subscription === period ? 'contained' : 'text'}
                      color='primary'
                      fullWidth
                      sx={{
                        textTransform: 'capitalize',
                        borderRadius: '8px',
                        py: { xs: 1.5, sm: 1 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        bgcolor: subscription === period ? 'primary.main' : 'transparent',
                        color: subscription === period ? 'white' : 'text.primary',
                        boxShadow: subscription === period ? 'rgba(99, 102, 241, 0.3) 0px 4px 12px' : 'none',
                        '&:hover': {
                          bgcolor: subscription === period ? 'primary.dark' : 'grey.100',
                        },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 1 }
                      }}
                      onClick={() => handleSubscriptionChange(period)}>
                      <Box component="span">
                        {period}
                      </Box>
                      {period === '12 Months' && (
                        <Chip 
                          label={`Save ${savings}%`}
                          size="small" 
                          sx={{ 
                            height: { xs: 16, sm: 20 }, 
                            fontSize: { xs: 9, sm: 10 },
                            bgcolor: 'success.main',
                            color: 'white',
                            minWidth: 'auto'
                          }} 
                        />
                      )}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Stack>

            {/* Inaugural Offer Alert - Mobile Optimized */}
            <Alert 
              severity="info" 
              icon={<FlashOnIcon />}
              sx={{ 
                mb: isMobile ? 2 : 4, 
                bgcolor: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)', 
                border: '2px solid',
                borderColor: 'primary.300',
                borderRadius: isMobile ? 1.5 : 2,
                boxShadow: 'rgba(99, 102, 241, 0.2) 0px 4px 16px',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}>
              <AlertTitle sx={{ 
                fontWeight: 700, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                color: 'primary.main',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                mb: 1
              }}>
                🚀 INAUGURAL LAUNCH PRICING!
              </AlertTitle>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  color: 'text.primary',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  lineHeight: 1.4
                }}>
                <strong>Welcome to the future of AI business tools!</strong> Launch celebration pricing: Monthly <strong>$20</strong> (reg. $50) • Annual <strong>$199</strong> (reg. $500)
                <br />
                <Box 
                  component="em" 
                  sx={{ 
                    color: '#1976d2', 
                    fontWeight: 600,
                    display: { xs: 'block', sm: 'inline' },
                    mt: { xs: 0.5, sm: 0 }
                  }}>
                  Join founding members and lock in these special rates!
                </Box>
              </Typography>
            </Alert>

            {/* Current Features Alert - Collapsible on Mobile */}
            <Box sx={{ mb: isMobile ? 2 : 4 }}>
              <Alert 
                severity="info" 
                sx={{ 
                  bgcolor: 'primary.50', 
                  border: '1px solid',
                  borderColor: 'primary.200',
                  borderRadius: isMobile ? 1.5 : 2
                }}>
                <Box sx={{ width: '100%' }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      cursor: isMobile ? 'pointer' : 'default'
                    }}
                    onClick={isMobile ? () => setShowDetails(!showDetails) : undefined}>
                    <AlertTitle sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.95rem', sm: '1.125rem' },
                      mb: { xs: 0, sm: 1 }
                    }}>
                      Currently Available Features
                    </AlertTitle>
                    {isMobile && (
                      <IconButton size="small" sx={{ mt: -0.5 }}>
                        {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </Box>
                  
                  <Collapse in={!isMobile || showDetails}>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        lineHeight: 1.4
                      }}>
                      <strong>AI Review Responses</strong> • <strong>Social Media Posts</strong> • 
                      <strong>FAQ Generation</strong> • <strong>Google Reviews</strong> • 
                      <strong>Content Scheduler</strong> • <strong>Document Q&A</strong> • 
                      <strong>Smart Ratings</strong> • <strong>Trust Chain</strong>
                      <br />
                      <Box 
                        component="em"
                        sx={{ 
                          display: { xs: 'block', sm: 'inline' },
                          mt: { xs: 0.5, sm: 0 }
                        }}>
                        Coming Soon: Voice Search, Multilingual, AI Automation, Reports
                      </Box>
                    </Typography>
                  </Collapse>
                </Box>
              </Alert>
            </Box>

            <Divider sx={{ mb: isMobile ? 2 : 4 }} />

            {/* Pricing Cards - Mobile Stack */}
            <Box>
              <Grid
                container
                spacing={{ xs: 2, md: 3 }}>
                {currentPlan &&
                  currentPlan.plan.map((plan, index) => (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      md={6}>
                      <PricingCard plan={plan} />
                    </Grid>
                  ))}
              </Grid>
            </Box>

            {/* Value Proposition Footer - Mobile Optimized */}
            <Box sx={{ mt: { xs: 4, md: 6 }, textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                fontWeight={600} 
                color="text.primary" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}>
                Why Choose Revfer AI?
              </Typography>
              <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography 
                      variant="h6" 
                      fontSize={{ xs: 14, sm: 16 }} 
                      fontWeight={600} 
                      color="primary.main" 
                      sx={{ mb: 1 }}>
                      Enterprise AI, Affordable
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Same AI capabilities large corporations pay thousands for, at small business prices.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography 
                      variant="h6" 
                      fontSize={{ xs: 14, sm: 16 }} 
                      fontWeight={600} 
                      color="success.main" 
                      sx={{ mb: 1 }}>
                      Save 10+ Hours/Week
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      AI automation handles reviews, customer questions, and provides instant insights.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography 
                      variant="h6" 
                      fontSize={{ xs: 14, sm: 16 }} 
                      fontWeight={600} 
                      color="warning.main" 
                      sx={{ mb: 1 }}>
                      Founding Member Benefits
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Exclusive inaugural pricing - up to 60% off for founding members believing in AI's future.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}